import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code2,
  Subscript,
  Superscript,
  Eraser,
  Sigma,
  Table2,
  ImageIcon,
  List,
  ListOrdered,
  ListTodo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
} from 'lucide-react';

import { Editor } from '@tiptap/react';
import clsx from 'clsx';
import { useState } from 'react';
import { TableGridPicker } from './table/TableGridPicker';
import { MathSymbolPicker } from './math/MathSymbolPicker';
import { FormulaInsertDialog } from './math/FormulaInsertDialog';

interface ToolbarProps {
  editor: Editor | null;
  onInsertMath: () => void;
  onInsertTable: () => void;
  imageUrls?: string[];
}

export const EditorToolbar = ({
  editor,
  onInsertMath,
  imageUrls,
}: ToolbarProps) => {
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [showListMenu, setShowListMenu] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [showMathMenu, setShowMathMenu] = useState(false);
  const [showMathInput, setShowMathInput] = useState(false);

  if (!editor) return null;

  const iconButton = (
    label: string,
    onClick: () => void,
    isActive: boolean,
    Icon: React.FC<any>,
  ) => (
    <button
      key={label}
      title={label}
      onClick={onClick}
      type="button"
      className={clsx(
        'p-2 rounded hover:bg-muted transition-colors',
        isActive && 'bg-muted text-primary',
      )}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="flex flex-wrap gap-2 px-3 py-2 bg-background text-foreground border border-border rounded-md relative z-[9]">
      {/* Default Image Upload */}
      {iconButton(
        'Upload Image',
        () => editor?.commands.uploadImage(),
        false,
        ImageIcon,
      )}
      {/* Text formatting */}
      {iconButton(
        'Bold',
        () => editor.chain().focus().toggleBold().run(),
        editor.isActive('bold'),
        Bold,
      )}
      {iconButton(
        'Italic',
        () => editor.chain().focus().toggleItalic().run(),
        editor.isActive('italic'),
        Italic,
      )}
      {iconButton(
        'Underline',
        () => editor.chain().focus().toggleUnderline().run(),
        editor.isActive('underline'),
        Underline,
      )}
      {iconButton(
        'Strikethrough',
        () => editor.chain().focus().toggleStrike().run(),
        editor.isActive('strike'),
        Strikethrough,
      )}
      {iconButton(
        'Code',
        () => editor.chain().focus().toggleCode().run(),
        editor.isActive('code'),
        Code2,
      )}
      {iconButton(
        'Clear Format',
        () => editor.chain().focus().unsetAllMarks().clearNodes().run(),
        false,
        Eraser,
      )}
      {iconButton(
        'Subscript',
        () => editor.chain().focus().toggleSubscript().run(),
        editor.isActive('subscript'),
        Subscript,
      )}
      {iconButton(
        'Superscript',
        () => editor.chain().focus().toggleSuperscript().run(),
        editor.isActive('superscript'),
        Superscript,
      )}

      {/* Align dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowAlignMenu(!showAlignMenu)}
          className="p-2 flex items-center gap-1 bg-muted hover:bg-muted/80 rounded transition-colors"
          title="Text Align"
        >
          <AlignLeft className="w-4 h-4" />
          <ChevronDown className="w-4 h-4" />
        </button>
        {showAlignMenu && (
          <div className="absolute top-full left-0 mt-1 flex bg-popover border border-border rounded shadow-md z-50">
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().setTextAlign('left').run();
                setShowAlignMenu(false);
              }}
              className="p-2 hover:bg-muted/70"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().setTextAlign('center').run();
                setShowAlignMenu(false);
              }}
              className="p-2 hover:bg-muted/70"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().setTextAlign('right').run();
                setShowAlignMenu(false);
              }}
              className="p-2 hover:bg-muted/70"
            >
              <AlignRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* List dropdown */}
      <div className="relative">
        <button
          onClick={() => setShowListMenu(!showListMenu)}
          className="p-2 flex items-center gap-1 bg-muted hover:bg-muted/80 rounded transition-colors"
          title="List Options"
          type="button"
        >
          <List className="w-4 h-4" />
          <ChevronDown className="w-4 h-4" />
        </button>
        {showListMenu && (
          <div className="absolute top-full left-0 mt-1 flex bg-popover border border-border rounded shadow-md z-50">
            <button
              onClick={() => {
                editor.chain().focus().toggleBulletList().run();
                setShowListMenu(false);
              }}
              className="p-2 hover:bg-muted/70"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                editor.chain().focus().toggleOrderedList().run();
                setShowListMenu(false);
              }}
              className="p-2 hover:bg-muted/70"
            >
              <ListOrdered className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                editor.chain().focus().toggleTaskList().run();
                setShowListMenu(false);
              }}
              className="p-2 hover:bg-muted/70"
            >
              <ListTodo className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Table dropdown with Grid Picker */}
      <div className="relative">
        <button
          onClick={() => setShowTableMenu(!showTableMenu)}
          className="p-2 flex items-center gap-1 bg-muted hover:bg-muted/80 rounded transition-colors"
          title="Insert Table"
          type="button"
        >
          <Table2 className="w-4 h-4" />
          <ChevronDown className="w-4 h-4" />
        </button>

        {showTableMenu && (
          <div
            className="absolute top-full left-0 mt-2 p-2 bg-popover border border-border rounded shadow-md z-50"
            onMouseLeave={() => setShowTableMenu(false)}
          >
            <TableGridPicker editor={editor} maxRows={6} maxCols={6} />
          </div>
        )}
      </div>

      <div className="relative">
        <button
          onClick={() => setShowMathMenu(!showMathMenu)}
          className="p-2 flex items-center gap-1 bg-muted hover:bg-muted/80 rounded transition-colors"
          title="Insert Math"
          type="button"
        >
          <Sigma className="w-4 h-4" />
          <ChevronDown className="w-4 h-4" />
        </button>

        {showMathMenu && (
          <div
            className="absolute top-full right-0 mt-2 p-2 w-[320px] bg-popover border border-border rounded shadow-md z-50"
            onMouseLeave={() => setShowMathMenu(false)}
          >
            <MathSymbolPicker editor={editor} />
          </div>
        )}
      </div>

      <div className="relative z-[99]">
        <button
          onClick={() => setShowMathInput(!showMathInput)}
          className="p-2 flex items-center gap-1 bg-muted hover:bg-muted/80 rounded transition-colors"
          title="Insert LaTeX Math"
          type="button"
        >
          <Sigma className="w-4 h-4" />
        </button>

        {showMathInput && (
          <FormulaInsertDialog
            editor={editor}
            onClose={() => setShowMathInput(false)}
          />
        )}
      </div>
    </div>
  );
};
